/**
 * Un desplegable NUESTRO, en vez del `<select>` del sistema.
 *
 * QUÉ PROBLEMA RESUELVE, que Javier reportó tres veces con la misma frase:
 * «se descuadran». La lista de un `<select>` no la dibuja la página, la
 * dibuja el sistema operativo, y por eso:
 *
 *  · no respeta el tema —en modo oscuro sale una lista clara del sistema—,
 *  · no respeta la tipografía ni el tamaño de letra de la aplicación,
 *  · aparece ANCLADA A LA OPCIÓN ELEGIDA y no al campo, así que en macOS se
 *    abre por encima del campo tapando lo de arriba, corrida a la izquierda:
 *    eso es lo que se ve «descuadrado», y no hay CSS que lo arregle.
 *
 * Esta lista es un `<ul>` de la página. Se dibuja debajo del campo, con los
 * colores y la letra de la aplicación, y se sube sola cuando no hay lugar
 * abajo.
 *
 * ES UN COMBOBOX DE VERDAD, con los mismos roles y teclas que
 * `CountryPicker` —del que sale este patrón—: flechas para moverse, Enter o
 * espacio para elegir, Escape para cerrar, Inicio y Fin para los extremos, y
 * escribir una letra salta a la opción que empieza con ella, que es lo que
 * hace el desplegable del sistema y lo que la gente ya tiene en los dedos.
 *
 * EL FOCO NO SE MUEVE A LA LISTA. Se queda en el botón y la opción resaltada
 * se anuncia con `aria-activedescendant`, igual que en el de países: mover el
 * foco de verdad obliga a devolverlo a mano al cerrar, y ese es el camino por
 * el que se pierde el foco al elegir con el teclado.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Un dato de al lado, en gris. Por ejemplo el prefijo de un país. */
  hint?: string;
}

interface SelectProps<T extends string> {
  id: string;
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Cuando el campo no tiene una `<label>` al lado. */
  'aria-label'?: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: true | undefined;
  /** Qué mostrar si el valor no está entre las opciones. */
  placeholder?: string;
}

export function Select<T extends string>({
  id,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = '',
  ...aria
}: SelectProps<T>): JSX.Element {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [arriba, setArriba] = useState(false);
  /**
   * Dónde dibujar la lista, en coordenadas de la ventana.
   *
   * LA LISTA NO VIVE ADENTRO DEL CAMPO, y esto es por una razón concreta: los
   * jugadores se listan en tablas que se desplazan de lado, y una caja que se
   * desplaza RECORTA lo que se sale de ella. Con la lista adentro, el
   * desplegable de categoría de un jugador aparecía cortado por abajo: se
   * veían dos opciones y media. Sacarla a `document.body` la saca de todo
   * recorte, y para eso hace falta decirle dónde ponerse.
   */
  const [donde, setDonde] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    minWidth: number;
  } | null>(null);
  const caja = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  /** Lo tecleado hace un instante, para saltar por letra. */
  const tecleado = useRef<{ texto: string; cuando: number }>({ texto: '', cuando: 0 });

  const elegido = options.find((o) => o.value === value) ?? null;

  // Un clic afuera cierra. Sin esto la lista se queda abierta tapando lo que
  // la persona está tratando de tocar.
  useEffect(() => {
    if (!open) return;
    const alClic = (event: MouseEvent): void => {
      const destino = event.target as Node;
      // La lista ya no está adentro del campo —vive en `document.body`—, así
      // que preguntar solo por el campo cerraría al tocar una opción.
      if (caja.current !== null && caja.current.contains(destino)) return;
      if (lista.current !== null && lista.current.contains(destino)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', alClic);
    return () => document.removeEventListener('mousedown', alClic);
  }, [open]);

  /**
   * ¿ABRE PARA ABAJO O PARA ARRIBA?
   *
   * Se mide ANTES de pintar (`useLayoutEffect`) para que no se vea el salto.
   * Un desplegable al final de la pantalla —el filtro de una lista larga, sin
   * ir más lejos— abriría hacia abajo contra el borde y se vería la mitad.
   */
  useLayoutEffect(() => {
    if (!open) return;

    const medir = (): void => {
      if (caja.current === null) return;
      const r = caja.current.getBoundingClientRect();
      const alto = Math.min(options.length * 40 + 16, 240);
      const haciaArriba = r.bottom + alto > window.innerHeight && r.top > alto;
      setArriba(haciaArriba);
      // Pegada al campo y DENTRO DE LA PANTALLA: un desplegable de una
      // columna de la derecha, anclado a secas, se dibujaría medio afuera.
      const ancho = Math.min(r.width, window.innerWidth - 16);
      setDonde({
        left: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)),
        // Hacia arriba se ancla por abajo y no por arriba: así crece contra
        // el campo con su alto de verdad, en vez de con el estimado.
        ...(haciaArriba ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
        minWidth: r.width,
      });
    };

    medir();

    /**
     * SI ALGO SE DESPLAZA, LA LISTA SE VUELVE A MEDIR. No se cierra.
     *
     * Estando fija en la ventana no viaja sola con el campo, así que al
     * desplazar quedaría flotando lejos de él. Lo primero que se intentó fue
     * cerrarla, y se cerraba SOLA AL ABRIRSE: dejar la opción elegida a la
     * vista usa `scrollIntoView`, que desplaza a algún antepasado —la tabla,
     * la página— y eso mismo disparaba el cierre. Volver a medir no tiene ese
     * problema y además se ve mejor.
     *
     * `capture` para enterarse también del desplazamiento de una caja de
     * adentro —la tabla que se corre de lado—, que no burbujea.
     */
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [open, options.length]);

  // La opción resaltada, siempre a la vista: con doce categorías la lista se
  // desplaza, y bajar con la flecha hasta una que no se ve no sirve de nada.
  useEffect(() => {
    if (!open || active < 0 || lista.current === null) return;
    const opcion = lista.current.children[active]?.querySelector('button');
    // `scrollIntoView` no existe en jsdom —ni en navegadores muy viejos— y sin
    // esta guarda la lista revienta al abrirse en las pruebas. Que no se
    // desplace no rompe nada: la opción sigue elegible.
    if (typeof opcion?.scrollIntoView === 'function') opcion.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  function abrir(dondeEmpezar: number): void {
    setOpen(true);
    setActive(dondeEmpezar);
  }

  function elegir(option: SelectOption<T>): void {
    onChange(option.value);
    setOpen(false);
  }

  /** Salta a la opción que empieza con lo tecleado. */
  function porLetra(letra: string): void {
    const ahora = Date.now();
    // Un segundo de memoria: escribir «ma» salta a «Masculino», pero dos
    // letras separadas por una pausa son dos búsquedas distintas.
    const texto = ahora - tecleado.current.cuando < 1000 ? tecleado.current.texto + letra : letra;
    tecleado.current = { texto, cuando: ahora };

    const buscado = texto.toLowerCase();
    const i = options.findIndex((o) => o.label.toLowerCase().startsWith(buscado));
    if (i === -1) return;
    if (open) setActive(i);
    else onChange(options[i]!.value);
  }

  function alTeclado(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const actual = active >= 0 ? active : options.findIndex((o) => o.value === value);
      if (!open) {
        abrir(Math.max(actual, 0));
        return;
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActive(Math.min(Math.max(actual + delta, 0), options.length - 1));
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      if (!open) return;
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : options.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        abrir(Math.max(options.findIndex((o) => o.value === value), 0));
        return;
      }
      const option = options[active];
      if (option !== undefined) elegir(option);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    // Una letra sola: saltar. Con Ctrl o Meta no, que son atajos del navegador.
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      porLetra(event.key);
    }
  }

  return (
    <div className="select" ref={caja}>
      <button
        type="button"
        id={id}
        className="select-button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        aria-activedescendant={
          open && active >= 0 && options[active] ? `${listId}-${options[active]!.value}` : undefined
        }
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : abrir(options.findIndex((o) => o.value === value)))}
        onKeyDown={alTeclado}
      >
        <span className="select-value">{elegido?.label ?? placeholder}</span>
        {/* La flechita es decorativa: lo que dice qué es esto es el rol. */}
        <span className="select-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open &&
        donde !== null &&
        createPortal(
        <ul
          className={arriba ? 'combo-list select-list up' : 'combo-list select-list'}
          id={listId}
          role="listbox"
          ref={lista}
          style={{
            position: 'fixed',
            left: `${donde.left}px`,
            ...(donde.top === undefined ? {} : { top: `${donde.top}px` }),
            ...(donde.bottom === undefined ? {} : { bottom: `${donde.bottom}px` }),
            right: 'auto',
            minWidth: `${donde.minWidth}px`,
            maxWidth: 'calc(100vw - 16px)',
            margin: 0,
          }}
        >
          {options.map((option, index) => (
            <li key={option.value} role="none">
              <button
                type="button"
                id={`${listId}-${option.value}`}
                role="option"
                aria-selected={option.value === value}
                className={index === active ? 'combo-option active' : 'combo-option'}
                // `onMouseDown` y no `onClick`: el clic llega después del
                // `blur`, y para entonces la lista ya se cerró.
                onMouseDown={(e) => {
                  e.preventDefault();
                  elegir(option);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="select-option-label">{option.label}</span>
                {option.hint !== undefined && <span className="help">{option.hint}</span>}
              </button>
            </li>
          ))}
        </ul>,
          document.body,
        )}
    </div>
  );
}
